/* @jsxImportSource react */

import { type ReactNode } from 'react';

import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { BypassTargetButton } from '@packages/shared/src/ui/BypassBlock/BypassTargetButton';

import { ResponsiveHeaderErrorFallback } from './ResponsiveHeaderErrorFallback';
import { ResponsiveHeaderSearchWrapper } from './ResponsiveHeader.SearchWrapper';
import { BypassNavigationLink } from './ResponsiveHeaderLayout.BypassBlocks';
import { BYPASS_TARGET_AFTER_MAIN_NAV, BYPASS_TO_MAIN_NAV } from './Header.ids';

export type ResponsiveHeaderLayoutProps = {
  logo: ReactNode;
  icons: ReactNode;
  /** Navigation is wrapped in a `position:relative` Box, to allow full-bleed break-out of the header */
  navigation: ReactNode;
  searchField: ReactNode;
};

export const ResponsiveHeaderLayout = ({
  logo,
  searchField,
  icons,
  navigation,
}: ResponsiveHeaderLayoutProps) => (
  <header
    className={cn(
      `grid [grid-template:'top_top_top_top'_auto_'button_logo_space_icons'_auto_'nav_nav_nav_nav'_1fr_'bottom_bottom_bottom_bottom'_auto_/_auto_auto_1fr_auto]`,
      'mx-auto w-full max-w-screen-xl items-center max-lg:shadow-sm lg:shadow-none',
      // Some backgrounds for the navigation extend outside of the default container width of the page}
      // The best way would be to accurately calculate the bleed, like calc(-0.5 * (var(--page-width-without-scrollbar) - var(--theme-width)))
      // Because scrollbar width is hard to determine at the moment using CSS only, we instead use large bleed and just clip it on the body (using overflow-x: hidden)
      'lg:[--background-bleed:-50vw]',
    )}
  >
    <div className="mx-1 [grid-area:logo] xl:mx-0">
      <ErrorBoundary fallback={<ResponsiveHeaderErrorFallback />}>{logo}</ErrorBoundary>
    </div>
    <ResponsiveHeaderSearchWrapper>
      <ErrorBoundary fallback={<ResponsiveHeaderErrorFallback />}>{searchField}</ErrorBoundary>
    </ResponsiveHeaderSearchWrapper>

    <BypassNavigationLink />
    <div
      tabIndex={-1}
      id={BYPASS_TO_MAIN_NAV}
      className="relative flex overflow-hidden [grid-area:icons]"
    >
      <ErrorBoundary fallback={<ResponsiveHeaderErrorFallback />}>{icons}</ErrorBoundary>
    </div>

    {/* workaround--flex flex-col is necessary to be here otherwise BypassAnchorButton gets focused out of the normal viewport flow */}
    <div className="relative flex flex-col [grid-area:button] lg:[grid-area:nav]">
      <ErrorBoundary fallback={<ResponsiveHeaderErrorFallback />}>{navigation}</ErrorBoundary>
      <BypassTargetButton id={BYPASS_TARGET_AFTER_MAIN_NAV} className="max-lg:hidden" />
    </div>
  </header>
);
